import { T } from 'gt-react';
import type { AgentRunSummary } from '@erp/rpc';
import { cn } from 'src/lib/utils';

type AgentRunStatus = AgentRunSummary['status'];

/**
 * Small status pill for a run (or the agent's idle state when it has none).
 * The one place run status is spelled and coloured on the agent page.
 */
export function RunStatusPill({
  status,
  notStarted = false,
  className,
}: {
  status: AgentRunStatus | null;
  /**
   * The start gate refused this run before it did anything. Shown in place
   * of the lifecycle status: "Run Finished" on a run that never ran is how a
   * blocked agent passes for a healthy idle one.
   */
  notStarted?: boolean;
  className?: string;
}) {
  const base =
    'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium';
  if (notStarted && status !== 'running') {
    return (
      <span
        className={cn(
          base,
          status === 'failed'
            ? 'border-red-200 bg-red-50 text-red-700'
            : 'border-stone-300 bg-stone-100 text-stone-600',
          className,
        )}
      >
        <span
          className={cn(
            'size-1.5 rounded-full',
            status === 'failed' ? 'bg-red-500' : 'bg-stone-400',
          )}
        />
        <T context="run status pill: the start gate refused the run before it did any work">
          Did not start
        </T>
      </span>
    );
  }
  if (status === 'running') {
    return (
      <span
        className={cn(base, 'border-sky-200 bg-sky-50 text-sky-700', className)}
      >
        <span className="size-1.5 animate-pulse rounded-full bg-sky-500" />
        <T context="run status pill">Running</T>
      </span>
    );
  }
  if (status === 'completed') {
    return (
      <span
        className={cn(
          base,
          'border-emerald-200 bg-emerald-50 text-emerald-700',
          className,
        )}
      >
        <span className="size-1.5 rounded-full bg-emerald-500" />
        <T context="run lifecycle finished independently of task outcomes">
          Run Finished
        </T>
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span
        className={cn(base, 'border-red-200 bg-red-50 text-red-700', className)}
      >
        <span className="size-1.5 rounded-full bg-red-500" />
        <T context="run status pill">Failed</T>
      </span>
    );
  }
  if (status === 'blocked') {
    return (
      <span
        className={cn(
          base,
          'border-amber-200 bg-amber-50 text-amber-700',
          className,
        )}
      >
        <span className="size-1.5 rounded-full bg-amber-500" />
        <T context="run status pill: interrupted run that could not resume because its agent was re-published">
          Blocked
        </T>
      </span>
    );
  }
  return (
    <span
      className={cn(
        base,
        'border-stone-200 bg-stone-50 text-stone-500',
        className,
      )}
    >
      <T context="agent status pill when it has never run / nothing is running">
        Idle
      </T>
    </span>
  );
}
